import { useMemo } from "react";
import { getGroups } from "../../services/keycloak";
export interface UserRole {
  isAdmin?: boolean;
  isMaster?: boolean;
  isHoAccountingSupervisor?: boolean;
  isHeadOfficeAccounting?: boolean;
  isBranch?: boolean;
  isBranchAccounting?: boolean;
  isBranchFinance?: boolean;
  isBranchManager?: boolean;
  isBranchSalePerson?: boolean;
  isBranchFinanceAndAccount?: boolean;
  /** การตลาด — head office's catalogue owner: products, pricebooks and costs. */
  isMarketing?: boolean;
  isBdcManager?: boolean;
  isBdcOperator?: boolean;
  /** Holds a BDC role and NO other GDP role — sees the BDC menus and nothing else. */
  isBdcOnly?: boolean;
}

export const ROLES = {
  ADMIN: "admin",
  MASTER: "master",
  BRANCH_ACCOUNTING: "branch accounting",
  BRANCH_FINANCE: "branch finance",
  BRANCH_SALES_PERSON: "branch sales person",
  BRANCH_CHECKER: "branch checker",
  BRANCH_MANAGER: "branch manager",
  HO_ACCOUNTING: "ho accounting",
  HO_ACCOUNTING_SUPERVISOR: "ho accounting supervisor",
  BRANCH_FINANCE_AND_ACCOUNT: "branch finance and accounting",
  MARKETING: "marketing",
  // BDC (7-Eleven DC) branch desk — the only roles a BDC branch user holds. หัวหน้า BDC edits
  // the BDC masters; เจ้าหน้าที่ BDC works the queue, imports, prints and exports.
  BDC_MANAGER: "bdc manager",
  BDC_OPERATOR: "bdc operator",
} as const;

const BDC_ROLES: string[] = [ROLES.BDC_MANAGER, ROLES.BDC_OPERATOR];

/**
 * True when the groups hold a BDC role and nothing else GDP knows about. Such a user is
 * confined to the BDC screens: the sidebar shows only BDC, every other path lands on the BDC
 * queue. A user holding a BDC role PLUS any other role keeps that other role's reach.
 */
export const isBdcOnlyGroups = (groups: string[] | undefined): boolean => {
  const normalized = (groups ?? []).map((group) => group.trim().toLowerCase());
  return (
    normalized.some((group) => BDC_ROLES.includes(group)) &&
    normalized.every((group) => BDC_ROLES.includes(group))
  );
};

export function useUserRole(): UserRole {
  const isAdmin = useMemo(() => getGroups()?.includes(ROLES.ADMIN), []);

  const isMaster = useMemo(() => getGroups()?.includes(ROLES.MASTER), []);

  const isBranch = useMemo(() => {
    return getGroups()?.some((group) => group.includes("branch"));
  }, []);

  const isBranchAccounting = useMemo(() => {
    return getGroups()?.includes(ROLES.BRANCH_ACCOUNTING);
  }, []);

  const isBranchFinance = useMemo(() => {
    return getGroups()?.includes(ROLES.BRANCH_FINANCE);
  }, []);

  const isBranchManager = useMemo(() => {
    return getGroups()?.includes(ROLES.BRANCH_MANAGER);
  }, []);

  const isBranchSalePerson = useMemo(() => {
    return getGroups()?.includes(ROLES.BRANCH_SALES_PERSON);
  }, []);

  // The supervisor grade of head-office accounting, on its own. Reserved for the
  // powers that are the supervisor's ALONE; anything the two grades share must ask
  // `isHeadOfficeAccounting` instead, or the plain grade silently loses it.
  const isHoAccountingSupervisor = useMemo(() => {
    return getGroups()?.includes(ROLES.HO_ACCOUNTING_SUPERVISOR);
  }, []);

  // Head-office accounting as a cohort — either grade. The supervisor holds
  // everything the plain accountant holds, so every existing head-office rule reads
  // this flag and neither grade has to be listed twice at each call site.
  const isHeadOfficeAccounting = useMemo(() => {
    const groups = getGroups();
    return (
      groups?.includes(ROLES.HO_ACCOUNTING) ||
      groups?.includes(ROLES.HO_ACCOUNTING_SUPERVISOR)
    );
  }, []);

  const isBranchFinanceAndAccount = useMemo(() => {
    return getGroups()?.includes(ROLES.BRANCH_FINANCE_AND_ACCOUNT);
  }, []);

  const isMarketing = useMemo(() => {
    return getGroups()?.includes(ROLES.MARKETING);
  }, []);

  const isBdcManager = useMemo(() => {
    return getGroups()?.includes(ROLES.BDC_MANAGER);
  }, []);

  const isBdcOperator = useMemo(() => {
    return getGroups()?.includes(ROLES.BDC_OPERATOR);
  }, []);

  const isBdcOnly = useMemo(() => isBdcOnlyGroups(getGroups()), []);

  return {
    isAdmin,
    isMaster,
    isBranch,
    isBranchAccounting,
    isBranchFinance,
    isBranchManager,
    isBranchSalePerson,
    isHoAccountingSupervisor,
    isHeadOfficeAccounting,
    isBranchFinanceAndAccount,
    isMarketing,
    isBdcManager,
    isBdcOperator,
    isBdcOnly,
  };
}
